import React from "react";
import RenderTableElement from "./RenderTableElement";
import {
  getElementRenderConfig,
  isImagePlaceholder,
  normalizeElementStyles,
  sanitizeParagraphHtml,
} from "./render-model";

const isTextWrapEnabled = (styles = {}) =>
  styles.textWrap === true || styles.textWrap === "true";

const getWrapAwareTextStyles = (styles = {}) => {
  const { textWrap, ...domSafeStyles } = styles;

  const hasBorderWidth =
    typeof domSafeStyles.borderWidth !== "undefined" &&
    String(domSafeStyles.borderWidth) !== "0" &&
    String(domSafeStyles.borderWidth) !== "0px";
  const hasNoBorderStyle =
    !domSafeStyles.borderStyle || domSafeStyles.borderStyle === "none";
  const borderSafeStyles =
    hasBorderWidth && hasNoBorderStyle
      ? { ...domSafeStyles, borderStyle: "solid", borderColor: domSafeStyles.borderColor || "#000000" }
      : domSafeStyles;

  if (isTextWrapEnabled(styles)) {
    return {
      ...borderSafeStyles,
      width: borderSafeStyles.width || "100%",
      whiteSpace: "normal",
      overflow: "visible",
      textOverflow: "clip",
      overflowWrap: "anywhere",
      wordBreak: "break-word",
    };
  }

  return {
    ...borderSafeStyles,
    whiteSpace: borderSafeStyles.whiteSpace || "nowrap",
    overflow: borderSafeStyles.overflow || "hidden",
    textOverflow: borderSafeStyles.textOverflow || "ellipsis",
  };
};

const RenderTemplateElement = ({
  element,
  mode = "canvas",
  onAssignFieldToColumn,
  onDeleteColumn,
  onAddColumn,
}) => {
  const renderConfig = getElementRenderConfig(element, mode);
  if (!renderConfig.visible) {
    return null;
  }

  const { placeholder, styles, content } = renderConfig;
  const imageSource = renderConfig.imageSource;
  const resolvedTextAlign = styles?.textAlign || styles?.["text-align"] || "left";
  const shouldUseTokenFallbackWidth =
    typeof renderConfig.element?.fieldPlaceHolder === "string" &&
    /^\{.+\}$/.test(renderConfig.element.fieldPlaceHolder) &&
    ![
      "{TABLE}",
      "{Paragraph}",
      "{PARAGRAPH_WITH_BORDER}",
      "{SECTION}",
      "{SECTIONN}",
      "{LINE}",
      "{COMPANY_LOGO}",
      "{WATERMARK}",
      "{BACKGROUND_IMAGE}",
      "{STATIC}",
      "{Secondary_Logo}",
    ].includes(renderConfig.element.fieldPlaceHolder);

  const fallbackTokenWidth =
    shouldUseTokenFallbackWidth && !styles.width
      ? resolvedTextAlign === "right"
        ? "18ch"
        : `${Math.max(10, String(renderConfig.element.fieldPlaceHolder).length - 2)}ch`
      : undefined;

  if (placeholder === "{TABLE}") {
    return (
      <RenderTableElement
        element={renderConfig.element}
        mode={mode}
        showPreviewRow={mode === "canvas"}
        onAssignFieldToColumn={onAssignFieldToColumn}
        onDeleteColumn={onDeleteColumn}
        onAddColumn={onAddColumn}
      />
    );
  }

  if (isImagePlaceholder(placeholder)) {
    const imageStyles = {
      ...styles,
      display: "block",
      boxSizing: "border-box",
      backgroundColor: "transparent",
      objectFit: styles.objectFit || styles["object-fit"] || "contain",
      objectPosition: styles.objectPosition || styles["object-position"] || "center",
    };

    if (placeholder === "{BACKGROUND_IMAGE}") {
      return (
        <img
          src={imageSource}
          alt="Background"
          crossOrigin="anonymous"
          style={{
            ...imageStyles,
            position: "absolute",
            inset: 0,
            width: imageStyles.width || "100%",
            height: imageStyles.height || "100%",
            zIndex: 0,
            pointerEvents: "none",
          }}
        />
      );
    }

    return (
      <img
        src={imageSource}
        alt={placeholder.replace(/[{}]/g, "")}
        crossOrigin="anonymous"
        style={imageStyles}
      />
    );
  }

  if (placeholder === "{LINE}") {
    const lineStyles = getWrapAwareTextStyles(styles);
    return (
      <div
        style={{
          ...lineStyles,
          minHeight: 0,
          height: lineStyles.height || "0px",
          width: lineStyles.width || "200px",
          borderTop: `${lineStyles.borderWidth || "1px"} ${
            lineStyles.borderStyle || "solid"
          } ${lineStyles.borderColor || "#000000"}`,
          borderRight: "none",
          borderBottom: "none",
          borderLeft: "none",
          backgroundColor: "transparent",
        }}
      />
    );
  }

  if (placeholder === "{Paragraph}") {
    const paragraphStyles = getWrapAwareTextStyles(styles);
    return (
      <div
        style={{
          ...paragraphStyles,
          width: "auto",
          boxSizing: "border-box",
          overflowWrap: "break-word",
          wordWrap: "break-word",
          whiteSpace: "normal",
          lineHeight: styles.lineHeight || "1.2",
          marginRight: "70px",
          padding: 0,
          position: "relative",
        }}
        dangerouslySetInnerHTML={{
          __html: sanitizeParagraphHtml(renderConfig.element.fieldName),
        }}
      />
    );
  }

  if (placeholder === "{PARAGRAPH_WITH_BORDER}") {
    const paragraphBorderStyles = getWrapAwareTextStyles(styles);
    return (
      <div
        className="paragraph-with-border"
        style={{
          ...paragraphBorderStyles,
          width: "auto",
          boxSizing: "border-box",
          overflowWrap: "break-word",
          wordWrap: "break-word",
          whiteSpace: "normal",
          lineHeight: styles.lineHeight || "1.2",
          marginRight: "70px",
          margin: "5px",
          padding: styles.padding || "5px",
          position: "relative",
          border: `1px solid ${styles.borderColor || "black"}`,
        }}
        dangerouslySetInnerHTML={{
          __html: sanitizeParagraphHtml(renderConfig.element.fieldName),
        }}
      />
    );
  }

  if (placeholder === "{SECTION}") {
    const normalizedStyles = normalizeElementStyles(renderConfig.element);
    const sectionStyles = getWrapAwareTextStyles(normalizedStyles);
    return (
      <div
        className="SECTION"
        style={sectionStyles}
        dangerouslySetInnerHTML={{ __html: renderConfig.element.fieldName }}
      />
    );
  }

  if (placeholder === "{SECTIONN}") {
    const normalizedStyles = normalizeElementStyles(renderConfig.element);
    const sectionNStyles = getWrapAwareTextStyles(normalizedStyles);
    const sectionFontFamily =
      normalizedStyles.fontFamily ||
      normalizedStyles["font-family"] ||
      "Arial, sans-serif";
    return (
      <div className="SECTION" style={{ ...sectionNStyles, fontFamily: sectionFontFamily }}>
        <style>{`.SECTION * { font-family: ${sectionFontFamily} !important; }`}</style>
        <div dangerouslySetInnerHTML={{ __html: renderConfig.element.fieldName }} />
      </div>
    );
  }

  const commonTextStyles = getWrapAwareTextStyles(styles);
  return (
    <div
      style={{
        ...commonTextStyles,
        width: commonTextStyles.width || fallbackTokenWidth || commonTextStyles.width,
        display: commonTextStyles.display || "block",
        boxSizing: "border-box",
      }}
    >
      {content}
    </div>
  );
};

export default RenderTemplateElement;
